
import React from 'react';
import { formatDistance, format, isPast, parseISO } from 'date-fns';
import { MoreHorizontal, MapPin, Users, DollarSign, Music, Clock, Ticket, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Gig } from '@/types/gigs';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface GigCardProps {
  gig: Gig;
}

const GigCard: React.FC<GigCardProps> = ({ gig }) => {
  const isUpcoming = !isPast(new Date(gig.date));
  const timeframe = isUpcoming 
    ? `In ${formatDistance(new Date(gig.date), new Date())}`
    : `${formatDistance(new Date(gig.date), new Date())} ago`;
  
  // Parse the time string to a valid date object
  const getTimeDate = () => {
    try {
      // If the time is in HH:MM format, create a full date string
      if (gig.time.includes(':')) {
        const [hours, minutes] = gig.time.split(':');
        const timeDate = new Date();
        timeDate.setHours(parseInt(hours, 10));
        timeDate.setMinutes(parseInt(minutes, 10));
        return timeDate;
      }
      
      // If it's already a date string, parse it
      return new Date(gig.time);
    } catch (error) {
      console.error('Error parsing time:', error);
      return new Date(); // Fallback to current time
    }
  };

  // Calculate ticket sales percentage if the gig has ticket data
  const hasTicketData = gig.ticketsSold !== undefined && gig.capacity > 0;
  const ticketPercentage = hasTicketData ? (gig.ticketsSold! / gig.capacity) * 100 : 0;
  
  return (
    <div className="p-4 hover:bg-muted/50 flex flex-col md:flex-row gap-4">
      {/* Date Block */}
      <div className="flex-shrink-0 w-full md:w-24 h-24 flex flex-col items-center justify-center rounded-lg 
                     border border-border bg-card text-center">
        <div className="text-sm font-medium text-muted-foreground">
          {format(new Date(gig.date), 'MMM')}
        </div>
        <div className="text-3xl font-bold">
          {format(new Date(gig.date), 'd')}
        </div>
        <div className="text-sm font-medium text-muted-foreground">
          {format(new Date(gig.date), 'EEE')}
        </div>
      </div>
      
      {/* Gig Details */}
      <div className="flex-grow">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{gig.name}</h3>
              {gig.source && (
                <Badge variant={
                  gig.source === 'songkick' ? 'neon-purple' : 
                  gig.source === 'eventbrite' ? 'neon-pink' : 
                  'outline'
                } className="text-xs">
                  {gig.source}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1 text-muted-foreground mt-1">
              <MapPin className="h-3.5 w-3.5" />
              <span className="text-sm">{gig.venue}, {gig.location}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant={
              gig.status === 'confirmed' ? 'soundcharts' :
              gig.status === 'pending' ? 'neon-purple' : 'default'
            }>
              {gig.status.charAt(0).toUpperCase() + gig.status.slice(1)}
            </Badge>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>View Details</DropdownMenuItem>
                <DropdownMenuItem>Edit Gig</DropdownMenuItem>
                <DropdownMenuItem>Manage Setlist</DropdownMenuItem>
                <DropdownMenuItem>Share Details</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>Manage Ticket Sales</DropdownMenuItem>
                <DropdownMenuItem>Sync with Calendar</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive">Cancel Gig</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="flex items-center gap-1.5 text-sm">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{format(getTimeDate(), 'h:mm a')}</span>
          </div>
          
          <div className="flex items-center gap-1.5 text-sm">
            <Music className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{gig.duration} set</span>
          </div>
          
          <div className="flex items-center gap-1.5 text-sm">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{gig.capacity} capacity</span>
          </div>
          
          <div className="flex items-center gap-1.5 text-sm">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
            <span>${gig.payment}</span>
          </div>
        </div>

        {/* Ticket Sales Progress (only shown if ticket data exists) */}
        {hasTicketData && (
          <div className="mt-3">
            <div className="flex justify-between text-sm mb-1">
              <div className="flex items-center gap-1.5">
                <Ticket className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Ticket Sales</span>
              </div>
              <span className="font-medium">{gig.ticketsSold} / {gig.capacity}</span>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Progress value={ticketPercentage} className="h-2" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{Math.round(ticketPercentage)}% of tickets sold</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        
        <div className="flex justify-between items-center mt-3">
          <div className="text-sm text-muted-foreground">
            {timeframe}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm">View Contract</Button>
            <Button size="sm">Manage Gig</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GigCard;
